import type { BreedId, ChallengeDef, ChangelogEntry } from './types';

// ── Challenges (unlock at the $1M win) ────────────────────────────────
export const CHALLENGES: readonly ChallengeDef[] = [
  { id: 'CH_MONEY', name: 'Tycoon Ladder', description: 'Earn more money, unlock new titles.', progress: 'lifetimeEarned',
    tiers: [
      { label: 'Tycoon', target: 10_000_000 },
      { label: 'Magnate', target: 100_000_000 },
      { label: 'Billionaire', target: 1_000_000_000 },
      { label: 'Trillionaire', target: 1_000_000_000_000 },
    ] },
  { id: 'CH_ALL_HENS', name: 'Full Coop', description: 'Own at least one of every hen breed.', progress: 'uniqueHenBreeds' },
  { id: 'CH_ALL_ROOSTERS', name: 'Full Rooster Wall', description: 'Own every rooster breed at least once.', progress: 'uniqueRoosterBreeds' },
  { id: 'CH_FIVE_FLOCKS', name: 'Five Flocks Strong', description: 'Run 5 full flocks of 15 hens each.', progress: 'fullFlocks', target: 5 },
  { id: 'CH_ALL_FIRST_KIND', name: 'Breed Diversity Master', description: 'Have every breed working as a first-of-kind somewhere in your farm.', progress: 'firstOfKindBreeds' },
  { id: 'CH_ALL_ACHIEVEMENTS', name: 'Completionist', description: 'Unlock every achievement.', progress: 'achievements' },
];

// ── Matriarch traits (earned by successful broody hatches) ────────────
export type TraitId = 'BROOD_PATCH_MASTERY' | 'FIERCE_MOTHER' | 'CLUTCH_INSTINCT' | 'FLOCK_MENTOR' | 'DEEP_BROODER';

export interface TraitDef { id: TraitId; name: string; description: string; flavor: string }

export const TRAITS: Record<TraitId, TraitDef> = {
  BROOD_PATCH_MASTERY: { id: 'BROOD_PATCH_MASTERY', name: 'Brood Patch Mastery', description: 'This hen lays 15% faster.',
    flavor: 'She plucked her breast feathers with surgical precision — maximum heat transfer, minimum fuss.' },
  FIERCE_MOTHER: { id: 'FIERCE_MOTHER', name: 'Fierce Mother', description: "This hen's eggs are worth 20% more.",
    flavor: 'Full dinosaur mode. Puffed up, growling, daring anyone to touch her precious clutch.' },
  CLUTCH_INSTINCT: { id: 'CLUTCH_INSTINCT', name: 'Clutch Instinct', description: 'Golden and silver eggs spawn 10% more often per Clutch Instinct hen (max +50%).',
    flavor: 'She turns each egg with her beak exactly 17 times a day. Nobody taught her that.' },
  FLOCK_MENTOR: { id: 'FLOCK_MENTOR', name: 'Flock Mentor', description: 'Every hen in her flock lays 5% faster.',
    flavor: 'The quiet tuk-tuk-tuk call that says "this is food, this is safe." The whole flock listens.' },
  DEEP_BROODER: { id: 'DEEP_BROODER', name: 'Deep Brooder', description: 'Her broods always succeed and award two traits when possible.',
    flavor: 'Eyes half-closed, barely breathing. She has entered the zone. Do not disturb.' },
};

export const MAX_TRAITS = 5;

/** Weighted trait pools per breed; unlisted breeds use DEFAULT_TRAIT_POOL. */
export const TRAIT_POOLS: Partial<Record<BreedId, [TraitId, number][]>> = {
  BUFF_ORPINGTON: [['FLOCK_MENTOR', 50], ['CLUTCH_INSTINCT', 30], ['BROOD_PATCH_MASTERY', 20]],
  PLYMOUTH_ROCK: [['FIERCE_MOTHER', 50], ['BROOD_PATCH_MASTERY', 30], ['DEEP_BROODER', 20]],
  SILKIE: [['DEEP_BROODER', 50], ['FLOCK_MENTOR', 30], ['CLUTCH_INSTINCT', 20]],
  COCHIN: [['BROOD_PATCH_MASTERY', 50], ['FIERCE_MOTHER', 30], ['FLOCK_MENTOR', 20]],
};
export const DEFAULT_TRAIT_POOL: [TraitId, number][] = [
  ['BROOD_PATCH_MASTERY', 20], ['FIERCE_MOTHER', 20], ['CLUTCH_INSTINCT', 20], ['FLOCK_MENTOR', 20], ['DEEP_BROODER', 20],
];

export const BROOD_SUCCESS: Partial<Record<BreedId, number>> = {
  BUFF_ORPINGTON: 0.7, PLYMOUTH_ROCK: 0.75, WYANDOTTE: 0.75, BRAHMA: 0.75, SPECKLED_SUSSEX: 0.75,
  MILLE_FLEUR_DUCCLE: 0.75, JAPANESE_BANTAM: 0.8, SILKIE: 1, COCHIN: 0.85,
};
export const DEFAULT_BROOD_SUCCESS = 0.7;

// ── Changelog (Stats tab) ─────────────────────────────────────────────
export const CHANGELOG: readonly ChangelogEntry[] = [
  { version: '1.0.0', date: '2026-09-25', title: 'The Rewrite',
    notes: [
      'Hen House, rebuilt from scratch on a new engine.',
      'One income model: every bonus you buy (roosters, coop upgrades, first-of-kind, breed powers) now raises the money your hens actually make.',
      'Every breed power does something. Every upgrade does something. Descriptions are generated from the real numbers.',
      'Two-act game: earn $1,000,000 to clear Stage 1, then grow a ten-rooster empire at an idle pace.',
      'Your farm keeps earning while you are away, capped by how many roosters you have.',
      'Rooster eggs now hatch cockerels that make your next rooster cheaper.',
    ] },
];
